import { handleActions, createAction } from "redux-actions";
import { api, notification } from "../../../services";
import history, { navigateTo404Page } from "../../../history";
import { UserFormat, UserRole } from "../../../models";
import { USER_SEARCH_FORMAT } from "../constants";

const USER_GET = "USER_GET";
const SEARCH_USER = "SEARCH_USER";
const USER_PROFILE_GET_BY_ID = "USER_PROFILE_GET_BY_ID";
const USER_GET_STATISTIC = "USER_GET_STATISTIC";
const BLOCK_USER = "BLOCK_USER";
const UNBLOCK_USER = "UNBLOCK_USER";
const BLOCK_REASONS_GET = "BLOCK_REASONS_GET";
const SHADOW_BLOCK_TRIGGER = "SHADOW_BLOCK_TRIGGER";
const DELETE_USER = "DELETE_USER";
const USER_SEND_ACTIVATION_EMAIL = "USER_SEND_ACTIVATION_EMAIL";
const USER_ACTIVATE_XML_SYNC_NOTIFICATIONS = "USER_ACTIVATE_XML_SYNC_NOTIFICATIONS";
const USER_AUTO_RENEWAL = "USER_AUTO_RENEWAL";
const USER_REDUCED_PRICE_TOGGLE = "USER_REDUCED_PRICE_TOGGLE";
const USER_TOGGLE_TWO_FACTOR = "USER_TOGGLE_TWO_FACTOR";

// Actions
export const getUsers = createAction(
  USER_GET,
  async (searchCriteria, page, itemPerPage, callback) => {
    try {
      const users = await api.User.list({
        ...searchCriteria,
        page,
        itemPerPage,
        format: USER_SEARCH_FORMAT.manage,
        roles: [UserRole.member],
      });

      callback && callback();

      return users;
    } catch (error) {
      callback && callback();

      return undefined;
    }
  }
);

const handleGetProfileByIdError = async (response) => {
  switch (response.status) {
    case 401:
      api.handleUnauthorizedError();
      break;

    case 404:
      notification.showError("Korisnik nije dostupan");
      navigateTo404Page();
      break;

    default:
      await api.handleCommonError(response);
      history.replace("/admin");
      break;
  }

  throw response;
};

export const getUserProfileById = createAction(
  USER_PROFILE_GET_BY_ID,
  async (id) => {
    try {
      return await api.Auth.userInfo(
        id,
        { format: UserFormat.profile },
        handleGetProfileByIdError
      );
    } catch (error) {
      return null;
    }
  }
);

export const getUserStatistics = createAction(
  USER_GET_STATISTIC,
  async (id) => {
    try {
      return await api.User.userStatistic(id);
    } catch (error) {
      return null;
    }
  }
);

export const blockUser = createAction(
  BLOCK_USER,
  async (id, blockReasonId, note, basedOn, usersData, callback) => {
    try {
      await api.User.block(id, blockReasonId, note, basedOn);
      callback && callback(true);
      return { id, blockReasonId, note, usersData };
    } catch (error) {
      callback && callback(false);
      return null;
    }
  }
);

export const getBlockReasons = createAction(BLOCK_REASONS_GET, async () => {
  try {
    return await api.User.getBlockReasons();
  } catch (error) {
    return [];
  }
});

export const unblockUser = createAction(UNBLOCK_USER, async (id, usersData, callback) => {
  try {
    await api.User.unblock(id);

    const users = usersData && usersData.data
    const mappedUsers = users.map((user) => {
      if (user.id === id) {
        return {
          ...user,
          isBlocked: false,
        };
      }

      return user;
    });

    callback && callback(true);


    return { users: { ...users, data: mappedUsers } };
  } catch (error) {
    callback && callback(false);

    return null;
  }
});

export const triggerShadowBlock = createAction(SHADOW_BLOCK_TRIGGER, async (id, isBanned, callback) => {
  try {
    await api.User.triggerShadowBlock(id, isBanned);
    callback && callback(true);
    return null;
  } catch (error) {
    callback && callback(false);

    return null;
  }
});

export const deleteUser = createAction(DELETE_USER, async (id) => {
  try {
    await api.User.deleteAsync(id);
    notification.show("Pokrenuto brisanje korisnika");
    return id;
  } catch (error) {
    notification.showError("Dogodila se greška");
    return null;
  }
});

export const activateXMLSyncNotifications = createAction(
  USER_ACTIVATE_XML_SYNC_NOTIFICATIONS,
  async (userId, activateXmlSync, callback) => {
    try {
      await api.User.activateXmlSync(
        userId,
        activateXmlSync,
      );

      callback && callback(true);

      activateXmlSync ? notification.show(
        "Obavijesti o XML sinkronizaciji aktivirane"
      ) : notification.show(
        "Obavijesti o XML sinkronizaciji deaktivirane"
      );
    } catch (error) {
      callback && callback(false);
      // skip
    }
  }
);

export const sendActivationEmail = createAction(
  USER_SEND_ACTIVATION_EMAIL,
  async (userId) => {
    try {
      await api.User.sendActivationEmail(userId);

      notification.show(
        "Reaktivacijski e-mail uspješno poslan"
      );
    } catch (error) {
      // skip
    }
  }
);

export const autoRenewal = createAction(
  USER_AUTO_RENEWAL,
  async (autoRenewal, id, callback) => {
    try {
      await api.Auth.autoRenewal(autoRenewal, id);

      notification.show(
        autoRenewal
          ? "Automatska obnova oglasa je aktivirana"
          : "Automatska obnova oglasa je deaktivirana"
      );

      callback && callback(true);

      return true;
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);

export const toggleHideAllReducedPrices = createAction(
  USER_REDUCED_PRICE_TOGGLE,
  async (user, callback: (success: boolean) => void) => {
    try {
      await api.Auth.reducedPrices(
        {
          userId: user.id,
          hideReducedPrices: !user.hideReducedPrices
        });

      callback && callback(true); // To get users after the update

      return true;
    } catch (error) {
      callback && callback(false);

      console.error("error on toggle reduced price visibility");
    }
  }
);

export const toggleTwoFactor = createAction(
  USER_TOGGLE_TWO_FACTOR,
  async (userId: string, twoFactorEnabled: boolean, callback?: (success: boolean) => void) => {
    try {
      await api.User.toggleTwoFactor(userId, twoFactorEnabled);

      notification.show(
        twoFactorEnabled
          ? "2FA je uključen"
          : "2FA je isključen"
      );

      callback && callback(true);

      return { userId, twoFactorEnabled };
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);

// Selectors
export const selectUsers = (state) => state.admin.user.users;
export const selectUser = (state, props) =>
  props.isAdminRoute ? state.admin.user.user : state.auth.user;
export const selectBlockReasons = (state) => state.admin.user.blockReasons;

// Reducer
const initialState = {
  users: {},
  searchCriteria: {},
  user: {},
  blockReasons: [],
};

const updateState = (state, { payload }) => ({
  ...state,
  ...payload,
});

const addUserStatistic = (state, { payload }) => ({
  ...state,
  user: { ...state.user, ...payload },
});

export default handleActions(
  {
    [USER_GET]: (state, { payload }) => ({
      ...state,
      users: payload,
    }),
    [SEARCH_USER]: updateState,
    [USER_PROFILE_GET_BY_ID]: (state, { payload }) => ({
      ...state,
      user: payload,
    }),
    [USER_GET_STATISTIC]: addUserStatistic,
    [BLOCK_USER]: (state, { payload }) => {
      if (!payload) return state;
      const { id, blockReasonId, note, usersData } = payload;
      const reason = state.blockReasons.find((r) => r.id === blockReasonId);
      const reasonName = reason ? reason.name : "";
      // Backend sastavlja "{reason}: {note}" — mirror that here for the
      // optimistic row so the list matches what a refetch would show.
      const composedNote = note ? reasonName + ": " + note : reasonName;
      const list = usersData && usersData.data;
      if (!list) return state;
      const nextData = list.map((u) =>
        u.id === id
          ? {
              ...u,
              isBlocked: true,
              blockReason: reason || null,
              blockNote: composedNote,
            }
          : u
      );
      return { ...state, users: { ...usersData, data: nextData } };
    },
    [UNBLOCK_USER]: updateState,
    [BLOCK_REASONS_GET]: (state, { payload }) => ({
      ...state,
      blockReasons: Array.isArray(payload) ? payload : [],
    }),
    [USER_TOGGLE_TWO_FACTOR]: (state, { payload }) => {
      if (!payload) {
        return state;
      }
      return {
        ...state,
        users: {
          ...state.users,
          data: state.users.data.map((user) =>
            user.id === payload.userId
              ? { ...user, twoFactorEnabled: payload.twoFactorEnabled }
              : user
          ),
        },
      };
    },
    [DELETE_USER]: (state, { payload }) => {
      if (!payload) {
        return state;
      }
      return {
        ...state,
        users: {
          ...state.users,
          data: state.users.data.filter((u) => u.id !== payload),
          count: state.users.count - 1,
        },
      };
    },
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/routes/Admin/ManageUserPage/user.ducks.ts